import React, { useState, useEffect, useRef } from 'react';
import {
  Star,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  GripVertical,
  ChevronUp,
  ChevronDown,
  StickyNote,
} from 'lucide-react';
import { updateAccount } from '../../services/firestoreService';
import { DEPARTMENT_SECTIONS } from '../KickoffAssessmentSections';

/**
 * Portal-facing Top Priorities builder for /portal/kickoff. A modern,
 * tap-to-rank take on the CRM's KickoffAssessmentSections: same account fields
 * (departmentAssessmentItems + departmentPriorityOrder), so the success manager
 * sees identical data on the CRM kickoff page. Panels are frosted so the
 * animated PortalBackground reads through, matching the Start pages.
 *
 * Interaction model: tapping a card cycles its priority Low → Medium → Top →
 * Low. Top-priority cards turn gold and appear in the ranked summary up top,
 * which supports drag-and-drop (and arrow buttons) for ordering.
 */

const PANEL =
  'rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/85 dark:bg-gray-800/85 backdrop-blur-sm shadow-sm';

const DEPT_PILL_COLORS = {
  sales: 'bg-red-500 text-white',
  operations: 'bg-blue-500 text-white',
  technology: 'bg-purple-500 text-white',
  finance: 'bg-green-500 text-white',
  marketing: 'bg-orange-500 text-white',
  hr: 'bg-yellow-500 text-white',
};

const LEVEL_LABELS = ['Low priority', 'Medium priority', 'Top Priority'];

const EMOJI_CHOICES = [
  '🚀', '⚡', '🔥', '💡', '🎯', '📈', '📉', '🔄', '⚙️', '🎨',
  '💻', '🌐', '📊', '🏆', '💎', '🌟', '📝', '📋', '📅', '⏰',
  '🔔', '📧', '💬', '📞', '🛒', '💰', '💳', '📦', '🚚', '🏢',
  '👥', '👤', '🎓', '🔐', '🔍', '📌', '🤖', '🧠', '🔧', '🔨',
  '🛠️', '✅', '⚠️', '📚', '📖', '✏️', '🧮', '💵', '🪙', '⚖️',
  '🧾', '💹', '📇', '📁', '🗂️', '🗓️', '📣', '📢', '🖥️', '⌨️',
  '🏗️', '🏠', '🏡', '🧰', '🪜', '🪚', '🧱', '🎪', '🤝', '🗺️',
  '🧭', '🔑', '🏅', '🥇', '📱', '☎️', '🖨️', '🗃️', '💼', '🌱',
];

const normalizeDeptItem = (item, defaultEmoji = '') => {
  if (typeof item === 'string') return { emoji: defaultEmoji, label: item, highlighted: false, priority: 0, note: '' };
  return {
    emoji: item.emoji || '',
    label: item.label ?? '',
    highlighted: Boolean(item.highlighted),
    priority: typeof item.priority === 'number' ? item.priority : 0,
    note: item.note || '',
  };
};

// Effective 0–2 level: legacy cards may carry highlighted=true with priority 0.
const itemLevel = (item) => (item.highlighted ? 2 : Math.min(Math.max(item.priority || 0, 0), 2));

const priorityKey = (deptId, label) => `${deptId}|${label}`;

// Standard (prepopulated) priorities are locked — no rename, no delete. Only
// custom cards the client added themselves are editable.
const STANDARD_LABELS = {};
DEPARTMENT_SECTIONS.forEach((s) => {
  STANDARD_LABELS[s.id] = new Set((s.items || []).map((it) => (typeof it === 'string' ? it : it.label)));
});
const isStandardItem = (deptId, label) => STANDARD_LABELS[deptId]?.has((label || '').trim());

export default function PortalKickoffPriorities({ accountId, account, darkMode, readOnly = false }) {
  const [deptItems, setDeptItems] = useState(() => {
    const initial = {};
    DEPARTMENT_SECTIONS.forEach((s) => {
      initial[s.id] = (s.items || []).map((it) => normalizeDeptItem(it, s.emoji));
    });
    return initial;
  });
  const [priorityOrder, setPriorityOrder] = useState([]);
  const [editingCard, setEditingCard] = useState(null); // { deptId, index }
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(null); // { deptId, index }
  const [dragIndex, setDragIndex] = useState(null);
  const [dragArmedKey, setDragArmedKey] = useState(null); // grip handle pressed — rows hold inputs, so only the handle starts a drag
  const [editingNoteKey, setEditingNoteKey] = useState(null); // Top Priorities row whose note input is open
  const [savedFlash, setSavedFlash] = useState(false);

  const topPanelRef = useRef(null);
  const sectionRefs = useRef({});
  const savedFlashTimer = useRef(null);
  const orderRef = useRef([]); // latest priorityOrder for drag-end persistence
  const deptItemsRef = useRef(deptItems); // latest items for note commit on blur
  // Which account we've already seeded from — parents load `account` once and
  // never refetch after a save, so re-seeding would replay stale data over
  // edits the user just made.
  const seededForRef = useRef(null);

  useEffect(() => {
    if (!account) return;
    if (seededForRef.current === (account.id ?? accountId)) return;
    seededForRef.current = account.id ?? accountId;
    const storedItems = account.departmentAssessmentItems || {};
    const items = {};
    DEPARTMENT_SECTIONS.forEach((s) => {
      if (Array.isArray(storedItems[s.id]) && storedItems[s.id].length > 0) {
        items[s.id] = storedItems[s.id].map((it) => normalizeDeptItem(it, s.emoji));
      } else {
        items[s.id] = (s.items || []).map((it) => normalizeDeptItem(it, s.emoji));
      }
    });
    setDeptItems(items);
    const order = Array.isArray(account.departmentPriorityOrder) ? [...account.departmentPriorityOrder] : [];
    setPriorityOrder(order);
    orderRef.current = order;
  }, [account, accountId]);

  useEffect(() => () => clearTimeout(savedFlashTimer.current), []);

  useEffect(() => {
    deptItemsRef.current = deptItems;
  }, [deptItems]);

  const flashSaved = () => {
    if (readOnly) return;
    setSavedFlash(true);
    clearTimeout(savedFlashTimer.current);
    savedFlashTimer.current = setTimeout(() => setSavedFlash(false), 1600);
  };

  const normalizeItemsForSave = (itemsState) => {
    const items = {};
    Object.keys(itemsState).forEach((k) => {
      items[k] = (itemsState[k] || []).map((it) => ({
        emoji: it.emoji || '',
        label: it.label || '',
        highlighted: Boolean(it.highlighted),
        priority: typeof it.priority === 'number' ? it.priority : 0,
        note: it.note || '',
      }));
    });
    return items;
  };

  const persistItems = (itemsState) => {
    if (!accountId) return;
    updateAccount(accountId, {
      departmentAssessmentItems: normalizeItemsForSave(itemsState),
      lastUpdated: new Date().toISOString(),
    })
      .then(flashSaved)
      .catch((e) => console.error('Error saving priorities:', e));
  };

  // Apply a new items map locally AND write it — both halves together, outside
  // any setState updater (StrictMode double-fires updaters).
  const applyItems = (next) => {
    setDeptItems(next);
    persistItems(next);
  };

  const persistOrder = (keys) => {
    if (!accountId) return;
    updateAccount(accountId, { departmentPriorityOrder: keys })
      .then(flashSaved)
      .catch((e) => console.error('Error saving priority order:', e));
  };

  // ----- Card interactions -------------------------------------------------

  const cyclePriority = (deptId, index) => {
    if (readOnly) return;
    const list = [...(deptItems[deptId] || [])];
    const item = list[index];
    if (!item) return;
    const next = (itemLevel(item) + 1) % 3;
    list[index] = { ...item, priority: next, highlighted: false };
    applyItems({ ...deptItems, [deptId]: list });
  };

  const updateDeptItem = (deptId, index, field, value) =>
    setDeptItems((p) => {
      const list = [...(p[deptId] || [])];
      if (list[index] === undefined) return p;
      list[index] = { ...list[index], [field]: value };
      return { ...p, [deptId]: list };
    });

  const setItemEmoji = (deptId, index, emoji) => {
    const list = [...(deptItems[deptId] || [])];
    if (list[index] === undefined) return;
    list[index] = { ...list[index], emoji };
    applyItems({ ...deptItems, [deptId]: list });
  };

  const addDeptItem = (deptId) => {
    if (readOnly) return;
    const list = [...(deptItems[deptId] || []), { emoji: '🌟', label: '', highlighted: false, priority: 0 }];
    setDeptItems((p) => ({ ...p, [deptId]: list }));
    setEditingCard({ deptId, index: list.length - 1 });
  };

  const removeDeptItem = (deptId, index) => {
    if (readOnly) return;
    applyItems({ ...deptItems, [deptId]: (deptItems[deptId] || []).filter((_, i) => i !== index) });
    setEditingCard(null);
    setEmojiPickerOpen(null);
  };

  // Finish inline editing: empty titles are dropped, everything else saves.
  const finishEditCard = () => {
    if (!editingCard) return;
    const { deptId, index } = editingCard;
    const list = deptItems[deptId] || [];
    const label = (list[index]?.label || '').trim();
    applyItems(label ? deptItems : { ...deptItems, [deptId]: list.filter((_, i) => i !== index) });
    setEditingCard(null);
    setEmojiPickerOpen(null);
  };

  // Note edits update local state as they type; the write happens once on
  // blur/Enter, from the ref (state in the closure would be one edit stale).
  const commitNote = () => {
    setEditingNoteKey(null);
    persistItems(deptItemsRef.current);
  };

  // ----- Top Priorities summary -------------------------------------------

  const topPriorities = (() => {
    const rows = [];
    DEPARTMENT_SECTIONS.forEach((sec) => {
      (deptItems[sec.id] || []).forEach((item, itemIndex) => {
        const label = (item.label || '').trim();
        if (!label || itemLevel(item) < 2) return;
        rows.push({
          key: priorityKey(sec.id, label),
          deptId: sec.id,
          deptName: sec.name,
          emoji: item.emoji,
          label,
          note: item.note || '',
          itemIndex,
        });
      });
    });
    rows.sort((a, b) => {
      const ia = priorityOrder.indexOf(a.key);
      const ib = priorityOrder.indexOf(b.key);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return 0;
    });
    return rows;
  })();

  const reorderKeys = (from, to) => {
    const keys = topPriorities.map((r) => r.key);
    const [moved] = keys.splice(from, 1);
    keys.splice(to, 0, moved);
    setPriorityOrder(keys);
    orderRef.current = keys;
    return keys;
  };

  const moveTopPriority = (index, dir) => {
    if (readOnly) return;
    const target = index + dir;
    if (target < 0 || target >= topPriorities.length) return;
    persistOrder(reorderKeys(index, target));
  };

  const handleDragEnter = (index) => {
    if (readOnly || dragIndex === null || index === dragIndex) return;
    reorderKeys(dragIndex, index);
    setDragIndex(index);
  };

  const handleDragEnd = () => {
    if (dragIndex === null) return;
    setDragIndex(null);
    persistOrder(orderRef.current.length ? orderRef.current : topPriorities.map((r) => r.key));
  };

  const deptTopCounts = {};
  DEPARTMENT_SECTIONS.forEach((sec) => {
    deptTopCounts[sec.id] = (deptItems[sec.id] || []).filter((it) => (it.label || '').trim() && itemLevel(it) >= 2).length;
  });
  const deptsCovered = DEPARTMENT_SECTIONS.filter((sec) => deptTopCounts[sec.id] > 0).length;

  const textMain = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';

  const scrollToSection = (deptId) =>
    sectionRefs.current[deptId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // ----- Render ------------------------------------------------------------

  return (
    <div className="space-y-6">
      <style>{`
        @keyframes bbaKickoffPop { 0% { transform: scale(0); } 70% { transform: scale(1.25); } 100% { transform: scale(1); } }
        .bba-kickoff-pop { animation: bbaKickoffPop 0.3s ease-out; }
      `}</style>

      {/* Department jump chips double as a progress tracker */}
      <div className="flex flex-wrap justify-center gap-2">
        {DEPARTMENT_SECTIONS.map((sec) => {
          const covered = deptTopCounts[sec.id] > 0;
          return (
            <button
              key={sec.id}
              type="button"
              onClick={() => scrollToSection(sec.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium backdrop-blur-sm transition-colors ${
                covered
                  ? 'bg-amber-50/90 border-amber-300 text-amber-800 dark:bg-amber-900/40 dark:border-amber-600 dark:text-amber-200'
                  : darkMode
                  ? 'bg-gray-800/75 border-gray-700 text-gray-300 hover:border-gray-500'
                  : 'bg-white/75 border-gray-200 text-gray-600 hover:border-gray-400'
              }`}
              title={`Jump to ${sec.name}`}
            >
              <span className="text-sm leading-none">{sec.emoji}</span>
              {sec.name}
              {covered && <Star className="w-3 h-3 fill-amber-500 text-amber-500" />}
            </button>
          );
        })}
      </div>

      {/* Ranked Top Priorities summary */}
      <section ref={topPanelRef} className={`${PANEL} p-5 sm:p-6 scroll-mt-24`}>
        <header className="text-center mb-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center mb-2">
            <Star className="w-6 h-6 fill-amber-500 text-amber-500" />
          </div>
          <h2 className={`text-xl font-bold text-center ${textMain}`}>Your Top Priorities</h2>
          <p className={`mt-1 text-sm text-center ${textMuted}`}>
            {topPriorities.length > 0
              ? readOnly
                ? 'Your ranked priorities across every department.'
                : 'Drag to put them in order — #1 is where we start.'
              : 'Tap the cards below to rank them. Cards at Top Priority turn gold and show up here.'}
          </p>
          {/* Coverage meter: one segment per department */}
          <div className="mt-3 mx-auto max-w-xs">
            <div className="flex gap-1">
              {DEPARTMENT_SECTIONS.map((sec) => (
                <div
                  key={sec.id}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    deptTopCounts[sec.id] > 0 ? 'bg-amber-400' : darkMode ? 'bg-gray-700' : 'bg-gray-200'
                  }`}
                  title={sec.name}
                />
              ))}
            </div>
            <p className={`mt-1.5 text-[11px] text-center ${textMuted}`}>
              {deptsCovered} of {DEPARTMENT_SECTIONS.length} departments covered
            </p>
          </div>
        </header>

        {topPriorities.length === 0 ? (
          <div
            className={`rounded-xl border-2 border-dashed px-4 py-8 text-center ${
              darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-300 text-gray-400'
            }`}
          >
            <p className="text-sm">Nothing ranked yet — your gold cards will stack up here.</p>
          </div>
        ) : (
          <ol className="space-y-2 max-w-2xl mx-auto">
            {topPriorities.map((row, idx) => {
              const noteOpen = !readOnly && editingNoteKey === row.key;
              return (
                <li
                  key={row.key}
                  draggable={!readOnly && dragArmedKey === row.key}
                  onDragStart={() => setDragIndex(idx)}
                  onDragEnter={() => handleDragEnter(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnd={() => {
                    setDragArmedKey(null);
                    handleDragEnd();
                  }}
                  className={`rounded-xl border px-3 py-2.5 transition-shadow ${
                    darkMode ? 'bg-gray-800/85 border-gray-600' : 'bg-white/90 border-gray-200'
                  } ${dragIndex === idx ? 'shadow-lg ring-2 ring-amber-400' : ''}`}
                >
                  <div className="flex items-center gap-2.5">
                    {!readOnly && (
                      <span
                        onMouseDown={() => setDragArmedKey(row.key)}
                        onMouseUp={() => setDragArmedKey(null)}
                        className="shrink-0 cursor-grab active:cursor-grabbing"
                        title="Drag to reorder"
                      >
                        <GripVertical className={`w-4 h-4 ${textMuted}`} />
                      </span>
                    )}
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                        idx < 3
                          ? 'bg-gradient-to-br from-amber-400 to-amber-500 text-white shadow-sm'
                          : darkMode
                          ? 'bg-gray-700 text-gray-300'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    {row.emoji && <span className="text-lg leading-none shrink-0">{row.emoji}</span>}
                    <span className={`flex-1 text-sm font-medium truncate ${textMain}`} title={row.label}>
                      {row.label}
                    </span>
                    <span className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium ${DEPT_PILL_COLORS[row.deptId] || 'bg-gray-500 text-white'}`}>
                      {row.deptName}
                    </span>
                    {!readOnly && (
                      <div className="shrink-0 flex flex-col">
                        <button
                          type="button"
                          onClick={() => moveTopPriority(idx, -1)}
                          disabled={idx === 0}
                          className={`p-0.5 rounded ${idx === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700'} ${textMuted}`}
                          title="Move up"
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveTopPriority(idx, 1)}
                          disabled={idx === topPriorities.length - 1}
                          className={`p-0.5 rounded ${idx === topPriorities.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700'} ${textMuted}`}
                          title="Move down"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Per-priority note: context for the BBA team, saved on the item */}
                  <div className="mt-1 pl-[62px] pr-2">
                    {noteOpen ? (
                      <input
                        type="text"
                        value={row.note}
                        onChange={(e) => updateDeptItem(row.deptId, row.itemIndex, 'note', e.target.value)}
                        onBlur={commitNote}
                        onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                        autoFocus
                        placeholder="Add a note — context, goals, anything we should know…"
                        className={`w-full text-xs bg-transparent border-b focus:outline-none py-0.5 ${
                          darkMode
                            ? 'text-gray-200 border-gray-600 focus:border-amber-400 placeholder-gray-500'
                            : 'text-gray-700 border-gray-300 focus:border-amber-400 placeholder-gray-400'
                        }`}
                      />
                    ) : row.note ? (
                      readOnly ? (
                        <p className={`flex items-start gap-1.5 text-xs italic ${textMuted}`}>
                          <StickyNote className="w-3 h-3 mt-0.5 shrink-0" />
                          <span>{row.note}</span>
                        </p>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingNoteKey(row.key)}
                          className={`flex items-start gap-1.5 text-left text-xs italic hover:underline ${textMuted}`}
                          title="Edit note"
                        >
                          <StickyNote className="w-3 h-3 mt-0.5 shrink-0" />
                          <span>{row.note}</span>
                        </button>
                      )
                    ) : (
                      !readOnly && (
                        <button
                          type="button"
                          onClick={() => setEditingNoteKey(row.key)}
                          className={`inline-flex items-center gap-1 text-xs ${textMuted} hover:text-amber-600 dark:hover:text-amber-400`}
                        >
                          <StickyNote className="w-3 h-3" /> Add note
                        </button>
                      )
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {/* Department sections */}
      {DEPARTMENT_SECTIONS.map((sec) => {
        const items = deptItems[sec.id] || [];
        return (
          <section
            key={sec.id}
            ref={(el) => { sectionRefs.current[sec.id] = el; }}
            className={`${PANEL} p-5 sm:p-6 scroll-mt-24`}
          >
            <header className="text-center mb-4">
              <div className="flex items-center justify-center gap-3">
                <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                  <span className="text-xl leading-none">{sec.emoji}</span>
                </span>
                <h3 className={`text-xl md:text-2xl font-bold tracking-tight leading-tight text-center ${textMain}`}>
                  {sec.name}
                </h3>
                <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              </div>
              {!readOnly && (
                <p className={`mt-1.5 text-xs text-center ${textMuted}`}>
                  Tap a card to raise its priority — Low, Medium, then gold Top Priority.
                </p>
              )}
            </header>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {items.map((item, i) => {
                const level = itemLevel(item);
                const isTop = level === 2;
                const isEditing = !readOnly && editingCard?.deptId === sec.id && editingCard?.index === i;
                const cardShell = isTop
                  ? 'bg-gradient-to-b from-amber-50 to-amber-100/80 border-amber-300 shadow-md shadow-amber-200/40 dark:from-amber-900/40 dark:to-amber-900/20 dark:border-amber-600 dark:shadow-amber-900/20'
                  : level === 1
                  ? `border-amber-300/70 dark:border-amber-700/70 ${darkMode ? 'bg-gray-800/85' : 'bg-white/85'}`
                  : darkMode
                  ? 'bg-gray-800/75 border-gray-700'
                  : 'bg-white/75 border-gray-200';

                const standard = isStandardItem(sec.id, item.label);

                if (isEditing) {
                  return (
                    <div
                      key={i}
                      className={`relative rounded-xl border p-3 min-h-[128px] flex flex-col items-center justify-between text-center backdrop-blur-sm ${cardShell}`}
                    >
                      <button
                        type="button"
                        onClick={finishEditCard}
                        className="absolute top-1.5 left-1.5 p-1 rounded text-green-600 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/30"
                        title="Done"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeDeptItem(sec.id, i)}
                        className="absolute top-1.5 right-1.5 p-1 rounded text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30"
                        title="Remove"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEmojiPickerOpen({ deptId: sec.id, index: i })}
                        className="mt-4 w-10 h-10 text-2xl border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 flex items-center justify-center hover:ring-2 hover:ring-amber-400"
                        title="Pick emoji"
                      >
                        {item.emoji || '😊'}
                      </button>
                      <input
                        type="text"
                        value={item.label}
                        onChange={(e) => updateDeptItem(sec.id, i, 'label', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && finishEditCard()}
                        autoFocus
                        className={`w-full mt-1 text-sm font-medium text-center bg-transparent border-b border-transparent hover:border-gray-400 focus:border-amber-400 focus:outline-none px-1 ${textMain}`}
                        placeholder="Priority"
                      />
                      <p className={`mt-1.5 text-[11px] ${textMuted}`}>Press Enter or ✓ to save</p>
                    </div>
                  );
                }

                return (
                  <div
                    key={i}
                    role={readOnly ? undefined : 'button'}
                    tabIndex={readOnly ? undefined : 0}
                    onClick={readOnly ? undefined : () => cyclePriority(sec.id, i)}
                    onKeyDown={
                      readOnly
                        ? undefined
                        : (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              cyclePriority(sec.id, i);
                            }
                          }
                    }
                    aria-label={readOnly ? undefined : `${item.label || 'Priority'} — ${LEVEL_LABELS[level]}. Tap to change.`}
                    className={`group relative rounded-xl border p-3 min-h-[128px] flex flex-col items-center justify-between text-center backdrop-blur-sm transition-all duration-200 ${cardShell} ${
                      !readOnly ? 'cursor-pointer select-none hover:shadow-md hover:-translate-y-0.5' : ''
                    }`}
                  >
                    {isTop && (
                      <span className="bba-kickoff-pop absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 shadow flex items-center justify-center">
                        <Star className="w-3.5 h-3.5 fill-white text-white" />
                      </span>
                    )}
                    {/* Standard priorities are locked — only custom cards get the pencil */}
                    {!readOnly && !standard && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingCard({ deptId: sec.id, index: i });
                        }}
                        className="absolute top-1.5 left-1.5 p-1 rounded text-gray-400 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700 transition-opacity"
                        title="Edit this priority"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <span className="text-2xl mt-2 leading-none">{item.emoji || '•'}</span>
                    <span className={`text-sm font-medium leading-snug ${textMain}`}>{item.label || '—'}</span>
                    <div className="w-full max-w-[104px]">
                      <div className="flex gap-1">
                        {[0, 1, 2].map((l) => (
                          <span
                            key={l}
                            className={`h-1.5 flex-1 rounded-full transition-colors ${
                              level >= l ? 'bg-amber-400' : darkMode ? 'bg-gray-700' : 'bg-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <p className={`mt-1 text-[11px] font-medium ${isTop ? 'text-amber-600 dark:text-amber-400' : textMuted}`}>
                        {isTop ? '⭐ Top Priority' : LEVEL_LABELS[level]}
                      </p>
                    </div>
                  </div>
                );
              })}

              {!readOnly && (
                <button
                  type="button"
                  onClick={() => addDeptItem(sec.id)}
                  className={`rounded-xl border-2 border-dashed p-3 min-h-[128px] flex flex-col items-center justify-center gap-1.5 transition-colors ${
                    darkMode
                      ? 'border-gray-600 text-gray-400 hover:border-amber-500 hover:text-amber-400'
                      : 'border-gray-300 text-gray-500 hover:border-amber-400 hover:text-amber-600'
                  }`}
                >
                  <Plus className="w-5 h-5" />
                  <span className="text-sm font-medium">Add your own</span>
                </button>
              )}
            </div>
          </section>
        );
      })}

      {/* Back to the ranked list */}
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => topPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 shadow-sm"
        >
          <ChevronUp className="w-4 h-4" /> Review Top Priorities
        </button>
      </div>

      {/* Autosave toast */}
      <div
        aria-hidden={!savedFlash}
        className={`fixed bottom-6 right-6 z-40 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg transition-all duration-300 ${
          savedFlash ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
        } ${darkMode ? 'bg-gray-800 border border-gray-700 text-green-400' : 'bg-white border border-gray-200 text-green-600'}`}
      >
        <Check className="w-3.5 h-3.5" /> Saved
      </div>

      {/* Emoji picker */}
      {emojiPickerOpen && !readOnly && (
        <div
          className="fixed inset-0 bg-gray-900/75 flex items-center justify-center z-50 p-4"
          onClick={() => setEmojiPickerOpen(null)}
        >
          <div
            className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-5 max-w-md w-full max-h-[70vh] overflow-hidden flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-center mb-4 relative">
              <h3 className={`text-lg font-semibold text-center ${textMain}`}>Pick an emoji</h3>
              <button
                type="button"
                onClick={() => setEmojiPickerOpen(null)}
                className={`absolute right-0 ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto grid grid-cols-8 gap-1.5">
              {EMOJI_CHOICES.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => {
                    setItemEmoji(emojiPickerOpen.deptId, emojiPickerOpen.index, emoji);
                    setEmojiPickerOpen(null);
                  }}
                  className="p-1.5 rounded-lg text-2xl hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
